import { ScanButton } from "@/components/ScanButton";
import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, ClipboardList, Trash2, FileText, Upload, Paperclip, Layers, Play } from "lucide-react";
import { Input } from "@/components/ui/input";
import { BulkRequestDialog } from "@/components/BulkRequestDialog";
import { ExtractViolationsButton, LicenseSuggestion } from "@/components/Violations";
import { ProgressBar, OwnerChip } from "@/components/ReportProgress";
import { bumpUnread } from "@/components/chat/UnreadBadge";
import { LineItemsEditor, linesTotal, toLinePayload, useCatalog, priceFor } from "@/components/billing/LineItemsEditor";
import { CompanyTag } from "@/components/CompanySwitcher";
import { DriverInfoCard } from "@/components/drivers/DriverInfoCard";
import { DriverPicker } from "@/components/drivers/DriverPicker";
import { PspConsentPanel } from "@/components/drivers/PspConsentPanel";

function statusBadge(s) {
  const map = {
    pending: "bg-indigo-50 text-indigo-700 border-indigo-200",
    "in-progress": "bg-amber-50 text-amber-800 border-amber-300",
    completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return <Badge className={`${map[s] || map.pending} border`}>{s}</Badge>;
}

export const displayTypes = (types) => {
  const rest = types.filter((t) => !t.startsWith("BGC"));
  return types.some((t) => t.startsWith("BGC")) ? ["BGC Bundle", ...rest] : rest;
};

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [catalog, setCatalog] = useState({});
  const [reports, setReports] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [myQueue, setMyQueue] = useState(false);
  const [picked, setPicked] = useState([]);
  const [markBusy, setMarkBusy] = useState(false);
  const markAlreadyBilled = async (flag) => {
    setMarkBusy(true);
    try {
      const { data } = await api.post("/reports/already-billed", { ids: picked, already_billed: flag });
      toast.success(`${data.updated} request${data.updated === 1 ? "" : "s"} ${flag ? "marked already billed" : "returned to billing"}${data.skipped ? ` · ${data.skipped} skipped (not completed)` : ""}`);
      setPicked([]); load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setMarkBusy(false); }
  };
  const [billFilter, setBillFilter] = useState("all");
  const queued = myQueue ? reports.filter((r) => r.started_by?.id === user?.id && r.status !== "completed" && r.status !== "cancelled") : reports;
  const visible = billFilter === "all" ? queued : queued.filter((r) => (billFilter === "already" ? !!r.already_billed : !r.already_billed && r.status !== "cancelled"));
  const alreadyCount = reports.filter((r) => r.already_billed).length;

  const [selected, setSelected] = useState([]);
  const [driverId, setDriverId] = useState("");
  const emptyNew = { first_name: "", last_name: "", ssn: "", dob: "", license_number: "", license_state: "", is_cdl: true, status: "active" };
  const [newDriver, setNewDriver] = useState(emptyNew);
  const setND = (k, v) => setNewDriver((d) => ({ ...d, [k]: v }));
  const [rush, setRush] = useState(false);
  const [notes, setNotes] = useState("");
  const emptyScope = { street: "", city: "", county: "", state: "", zip: "", ssn: "" };
  const [scope, setScope] = useState(emptyScope);
  const setSC = (k, v) => setScope((s) => ({ ...s, [k]: v }));
  const isBgc = selected.some((k) => k.startsWith("BGC"));
  const scopeOnFile = !!(driverId && drivers.find((d) => d.id === driverId)?.address);
  useEffect(() => {
    if (!isBgc) return;
    const d = driverId ? drivers.find((x) => x.id === driverId) : null;
    if (d) setScope({ ...emptyScope, ...(d.address || {}), ssn: d.ssn || "" });
    else setScope((s) => ({ ...s, ssn: newDriver.ssn || s.ssn }));
  }, [isBgc, driverId, newDriver.ssn]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasPrices = Object.values(catalog).some((v) => v.price != null);
  const [statePrices, setStatePrices] = useState({});
  const orderState = (driverId ? drivers.find((d) => d.id === driverId)?.license_state : newDriver.license_state) || "";
  const shownPrice = (k) => priceFor(k, catalog, statePrices, orderState.toUpperCase());
  const estimate = selected.reduce((s, k) => s + shownPrice(k), 0) + (rush ? Number(catalog.RUSH?.price) || 0 : 0);

  const load = useCallback(async () => {
    try {
      const [{ data: cat }, { data: r }, { data: dr }, { data: sp }] = await Promise.all([
        api.get("/reports/catalog"),
        api.get("/reports"),
        api.get("/drivers"),
        api.get("/reports/catalog/state-prices"),
      ]);
      setCatalog(cat); setReports(r); setDrivers(dr); setStatePrices(sp);
      if (isAdmin) { const { data: c } = await api.get("/customers"); setCustomers(c); }
    } catch (err) { toast.error(formatApiError(err)); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user && !isAdmin) api.post("/reports/mark-seen").then(() => bumpUnread()).catch(() => {}); }, [user, isAdmin]);

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) { toast.error("Select at least one report type"); return; }
    try {
      await api.post("/reports", {
        driver_id: driverId || null,
        new_driver: driverId ? null : newDriver,
        report_types: selected,
        rush,
        notes,
        bgc_scope: isBgc ? scope : null,
      });
      toast.success(driverId ? "Report request submitted" : newDriver.status === "applicant" ? "Report request submitted · added to Applicants on the Drivers page" : "Report request submitted · driver added to Drivers");
      setOpen(false); setSelected([]); setRush(false); setNotes(""); setDriverId(""); setNewDriver(emptyNew); setScope(emptyScope);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="reports-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="tag-mono text-blue-700 mb-1">À LA CARTE</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900">Report Requests</h1>
          <p className="text-slate-600 mt-1">Order PSP, MVR, background checks and more.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <BulkRequestDialog catalog={catalog} isAdmin={isAdmin} customers={customers} onDone={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800" data-testid="request-report-button">
              <Plus className="h-4 w-4 mr-2" /> New Report Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Request Reports</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Driver</Label>
                <DriverPicker drivers={drivers} customers={customers} value={driverId} onChange={setDriverId} groupBy={isAdmin ? "company" : "none"} />
                {driverId && <div className="mt-2"><DriverInfoCard driverId={driverId} isAdmin={isAdmin} />{selected.includes("PSP") && <div className="mt-2"><PspConsentPanel driver={{ id: driverId }} /></div>}</div>}
                {!driverId && (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-3" data-testid="report-new-driver-form">
                    <div className="col-span-2 tag-mono text-slate-500">NEW DRIVER · will be added to your Drivers list</div>
                    <div className="col-span-2 rounded-md border border-slate-200 bg-white p-2.5" data-testid="report-new-driver-status">
                      <Label className="text-xs">Add as</Label>
                      <div className="mt-1.5 inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5 text-sm" role="radiogroup">
                        {[["active", "Active driver"], ["applicant", "Applicant (pending hire)"]].map(([v, l]) => (
                          <button key={v} type="button" onClick={() => setND("status", v)} className={`px-3 py-1 rounded-full font-medium transition-colors ${newDriver.status === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`report-new-driver-status-${v}`}>{l}</button>
                        ))}
                      </div>
                      {newDriver.status === "applicant" && <p className="mt-1.5 text-xs text-slate-500">Applicants are listed separately from active drivers, stay out of the random pool and alerts, and can be marked Hired or Not hired once the reports come back.</p>}
                    </div>
                    <div><Label className="text-xs">First name</Label><Input required value={newDriver.first_name} onChange={(e) => setND("first_name", e.target.value)} data-testid="report-manual-first-name-input" /></div>
                    <div><Label className="text-xs">Last name</Label><Input required value={newDriver.last_name} onChange={(e) => setND("last_name", e.target.value)} data-testid="report-manual-last-name-input" /></div>
                    <div><Label className="text-xs">Social Security number</Label><Input required placeholder="###-##-####" inputMode="numeric" maxLength={11} value={newDriver.ssn} onChange={(e) => setND("ssn", e.target.value)} data-testid="report-manual-ssn-input" /></div>
                    <div><Label className="text-xs">Date of birth</Label><Input required type="date" value={newDriver.dob} onChange={(e) => setND("dob", e.target.value)} data-testid="report-manual-dob-input" /></div>
                    <div><Label className="text-xs">License number</Label><Input required value={newDriver.license_number} onChange={(e) => setND("license_number", e.target.value)} data-testid="report-manual-license-input" /></div>
                    <div><Label className="text-xs">Issue state</Label><Input required maxLength={2} placeholder="TX" value={newDriver.license_state} onChange={(e) => setND("license_state", e.target.value.toUpperCase())} data-testid="report-manual-state-input" /></div>
                    <div className="col-span-2 flex items-center justify-between rounded-md border border-slate-200 bg-white p-2.5">
                      <Label className="text-sm">Is CDL?</Label>
                      <Switch checked={newDriver.is_cdl} onCheckedChange={(v) => setND("is_cdl", v)} data-testid="report-manual-is-cdl-toggle" />
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="mb-2 block">Select Reports</Label>
                <div className="space-y-2">
                  {Object.entries(catalog).filter(([k, v]) => k !== "RUSH" && !(v.per_unit && k.startsWith("BGC"))).sort((a, b) => a[1].label.localeCompare(b[1].label)).map(([k, v]) => {
                    const bundle = k === "BGC" ? Object.keys(catalog).filter((c) => catalog[c].per_unit) : [];
                    const toggle = (c) => setSelected(c ? [...new Set([...selected, k, ...bundle])] : selected.filter((x) => x !== k && !bundle.includes(x)));
                    return (
                    <label key={k} className="flex flex-col rounded-md border border-slate-200 p-3 cursor-pointer hover:bg-slate-50" data-testid={`report-type-option-${k}`}>
                      <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Checkbox
                          checked={selected.includes(k)}
                          onCheckedChange={toggle}
                          data-testid={`report-checkbox-${k}`}
                        />
                        <div>
                          <div className="font-medium text-slate-900 text-sm">{v.label}</div>
                          <div className="tag-mono text-slate-500">{k}</div>
                        </div>
                      </div>
                      {v.price != null && <span className="font-mono text-sm text-slate-700" data-testid={`report-price-${k}`}>{bundle.length ? "from " : ""}${shownPrice(k).toFixed(2)}{v.state_fee && orderState && <span className="text-[10px] text-slate-400 ml-1">{orderState.toUpperCase()}</span>}</span>}
                      {v.price == null && v.price_hidden && bundle.length > 0 && <span className="text-[11px] text-slate-500 italic" data-testid={`report-price-${k}`}>priced on invoice</span>}
                      </div>
                      {bundle.length > 0 && (
                        <div className="mt-2 ml-7 rounded border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-[11px] text-sky-900" data-testid="bgc-bundle-note">
                          <b>Background Check Bundle</b> — includes <b>county</b>, <b>statewide</b> and <b>national</b> criminal searches. <b>Your invoice will itemize the actual number of counties and states searched</b> (based on the driver's address and employment history), so the final charge is set when the report is completed{v.price != null && bundle.some((c) => catalog[c].price != null) ? ` — ${bundle.filter((c) => catalog[c].price != null).map((c) => `${catalog[c].label.replace(/\s*\(per .*\)/, "")} $${Number(catalog[c].price || 0).toFixed(2)}/${catalog[c].per_unit}`).join(", ")}` : ""}.
                        </div>
                      )}
                    </label>
                  ); })}
                </div>
                {hasPrices && (
                  <div className="mt-3 flex items-center justify-between rounded-md bg-slate-900 text-white px-4 py-3" data-testid="report-estimate">
                    <div><div className="tag-mono text-slate-400">ESTIMATED CHARGE</div><div className="text-xs text-slate-400">For reference only · invoiced via QuickBooks, no payment collected here</div></div>
                    <div className="text-2xl font-bold font-mono">${estimate.toFixed(2)}</div>
                  </div>
                )}
              </div>

              {isBgc && (
                <div className="rounded-md border border-sky-200 bg-sky-50 p-3 grid grid-cols-2 gap-3" data-testid="bgc-scope-panel">
                  <div className="col-span-2">
                    <div className="tag-mono text-sky-800">BACKGROUND CHECK SCOPE · REQUIRED</div>
                    <div className="text-xs text-sky-900 mt-0.5">{scopeOnFile ? "Pre-filled from the driver's record — confirm or update before submitting." : "Driver's current address and Social Security number — a background check cannot be completed without them."}</div>
                  </div>
                  <div className="col-span-2"><Label className="text-xs">Street address</Label><Input required value={scope.street} onChange={(e) => setSC("street", e.target.value)} data-testid="bgc-street-input" /></div>
                  <div><Label className="text-xs">City</Label><Input required value={scope.city} onChange={(e) => setSC("city", e.target.value)} data-testid="bgc-city-input" /></div>
                  <div><Label className="text-xs">County</Label><Input required value={scope.county} onChange={(e) => setSC("county", e.target.value)} data-testid="bgc-county-input" /></div>
                  <div><Label className="text-xs">State</Label><Input required maxLength={2} placeholder="TX" value={scope.state} onChange={(e) => setSC("state", e.target.value.toUpperCase())} data-testid="bgc-state-input" /></div>
                  <div><Label className="text-xs">ZIP</Label><Input required inputMode="numeric" maxLength={10} value={scope.zip} onChange={(e) => setSC("zip", e.target.value)} data-testid="bgc-zip-input" /></div>
                  <div className="col-span-2"><Label className="text-xs">Social Security number{scope.ssn.startsWith("***") && <span className="ml-2 text-sky-700 font-normal">on file · leave as-is or retype to correct</span>}</Label><Input required placeholder="###-##-####" inputMode="numeric" maxLength={11} value={scope.ssn} onChange={(e) => setSC("ssn", e.target.value)} onFocus={(e) => { if (e.target.value.startsWith("***")) setSC("ssn", ""); }} data-testid="bgc-ssn-input" /></div>
                </div>
              )}

              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div>
                  <Label className="text-sm">Rush Processing</Label>
                  <div className="text-xs text-slate-500">Priority handling</div>
                </div>
                <Switch checked={rush} onCheckedChange={setRush} data-testid="report-rush-toggle" />
              </div>
              {catalog.RUSH?.price != null && rush && <div className="text-xs text-slate-500 -mt-2">Rush fee: ${Number(catalog.RUSH.price).toFixed(2)}</div>}

              <div><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the admin should know" /></div>

              <DialogFooter><Button type="submit" className="bg-blue-700 hover:bg-blue-800" data-testid="report-submit-button">Submit Request</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {isAdmin && <BatchBar reports={reports} onChanged={load} me={user} />}

      {isAdmin && (
        <div className="flex items-center gap-2">
          <Button size="sm" variant={myQueue ? "default" : "outline"} className={myQueue ? "bg-blue-700 hover:bg-blue-800" : ""} onClick={() => setMyQueue((v) => !v)} data-testid="my-queue-toggle">My queue{myQueue ? ` (${visible.length})` : ""}</Button>
          <span className="text-xs text-slate-500">{myQueue ? "Open requests you started" : "Showing all requests"}</span>
          <div className="inline-flex rounded-full border border-slate-200 bg-white p-0.5 text-xs ml-2" role="group" data-testid="billing-filter">
            {[["all", "All"], ["billable", "Billable"], ["already", `Already billed${alreadyCount ? ` (${alreadyCount})` : ""}`]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setBillFilter(v)} className={`px-3 py-1 rounded-full font-medium transition-colors ${billFilter === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`billing-filter-${v}`}>{l}</button>
            ))}
          </div>
          {picked.length > 0 && (
            <div className="ml-auto flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs" data-testid="already-billed-bar">
              <span className="font-medium text-slate-800">{picked.length} selected</span>
              <Button size="sm" className="h-7 bg-slate-900 hover:bg-slate-800" disabled={markBusy} onClick={() => markAlreadyBilled(true)} data-testid="mark-already-billed-button">Mark already billed</Button>
              <Button size="sm" variant="outline" className="h-7" disabled={markBusy} onClick={() => markAlreadyBilled(false)} data-testid="unmark-already-billed-button">Return to billing</Button>
              <button type="button" className="text-slate-500 hover:underline" onClick={() => setPicked([])} data-testid="already-billed-clear">Clear</button>
            </div>
          )}
        </div>
      )}

      <Card>
        <Table data-testid="reports-table">
          <TableHeader>
            <TableRow>
              {isAdmin && <TableHead className="w-8"><Checkbox checked={visible.filter((r) => r.status === "completed").length > 0 && visible.filter((r) => r.status === "completed").every((r) => picked.includes(r.id))} onCheckedChange={(c) => setPicked(c ? visible.filter((r) => r.status === "completed").map((r) => r.id) : [])} title="Select all completed requests shown" data-testid="pick-all-completed" /></TableHead>}
              <TableHead>Requested</TableHead>
              {isAdmin && <TableHead>Requester</TableHead>}
              <TableHead>Driver</TableHead>
              <TableHead>Reports</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Progress</TableHead>
              {isAdmin && <TableHead>Owner</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (<TableRow><TableCell colSpan={isAdmin ? 8 : 5} className="text-center py-8 text-slate-500">{myQueue ? "Nothing in your queue — click Start working on a request to claim it." : billFilter === "already" ? "No requests are marked already billed." : "No requests yet."}</TableCell></TableRow>)}
            {visible.map((r) => (
              <TableRow key={r.id} className={`cursor-pointer ${r.rush && r.status === "pending" && !r.started_by ? "bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400" : ""}`} onClick={() => setDetail(r)} data-testid={`report-row-${r.id}`} data-rush-unstarted={r.rush && r.status === "pending" && !r.started_by ? "true" : "false"}>
                {isAdmin && <TableCell onClick={(e) => e.stopPropagation()} className="w-8">{r.status === "completed" && <Checkbox checked={picked.includes(r.id)} onCheckedChange={(c) => setPicked((p) => (c ? [...p, r.id] : p.filter((x) => x !== r.id)))} data-testid={`pick-report-${r.id}`} />}</TableCell>}
                <TableCell className="font-mono text-xs">{new Date(r.created_at).toLocaleString()}{r.rush && <span className="ml-2 inline-flex items-center rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold text-slate-900 not-italic" data-testid={`rush-tag-${r.id}`}>RUSH</span>}</TableCell>
                {isAdmin && <TableCell><div className="font-medium">{r.requester_name}</div><div className="text-xs text-slate-500 flex items-center gap-1"><CompanyTag customerId={r.requester_id} testid={`report-company-${r.id}`} />{r.requester_company && <span className="sr-only">{r.requester_company}</span>}</div></TableCell>}
                <TableCell><div className="flex items-center gap-1.5 flex-wrap">{r.driver_name || "—"}{!isAdmin && (user?.companies?.length > 1) && <CompanyTag customerId={r.requester_id} testid={`report-company-${r.id}`} />}</div></TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{displayTypes(r.report_types).map((rt) => (<Badge key={rt} variant="outline" className="tag-mono">{rt}</Badge>))}</div>{r.rush && <Badge className="bg-amber-50 text-amber-800 border-amber-300 mt-1">RUSH</Badge>}</TableCell>
                <TableCell>{statusBadge(r.status)}{r.source === "off-portal" && <Badge className="ml-2 bg-violet-50 text-violet-700 border border-violet-200" title={`Received via ${r.channel}`} data-testid={`report-offportal-${r.id}`}>Off-portal</Badge>}{r.already_billed && <Badge className="ml-2 bg-slate-100 text-slate-600 border border-slate-300" title="Entered for the record — not on this month's statement" data-testid={`report-already-billed-${r.id}`}>Already billed</Badge>}{r.batch_id && <Badge className="ml-2 bg-slate-100 text-slate-700 border border-slate-200" title="Part of a bulk request"><Layers className="h-3 w-3 mr-1" />Bulk</Badge>}{r.files?.length > 0 && <Badge className="ml-2 bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`report-files-badge-${r.id}`}><Paperclip className="h-3 w-3 mr-1" />{r.files.length} result{r.files.length > 1 ? "s" : ""}</Badge>}</TableCell>
                <TableCell><ProgressBar r={r} compact /></TableCell>
                {isAdmin && <TableCell><OwnerChip r={r} /></TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ReportDetailDialog report={detail} onClose={() => { setDetail(null); load(); }} isAdmin={isAdmin} customers={customers} />
    </div>
  );
}

function BatchBar({ reports, onChanged, me }) {
  const [busy, setBusy] = useState(null);
  const batches = Object.values(reports.reduce((acc, r) => {
    if (!r.batch_id) return acc;
    const b = acc[r.batch_id] || (acc[r.batch_id] = { id: r.batch_id, label: r.notes, company: r.requester_company || r.requester_name, created_at: r.created_at, types: r.report_types, count: 0, statuses: {}, owners: {} });
    b.count += 1; b.statuses[r.status] = (b.statuses[r.status] || 0) + 1;
    if (r.started_by && r.status !== "completed" && r.status !== "cancelled") { const o = b.owners[r.started_by.id] || (b.owners[r.started_by.id] = { ...r.started_by, at: r.started_at, n: 0 }); o.n += 1; }
    return acc;
  }, {})).filter((b) => (b.statuses.pending || 0) + (b.statuses["in-progress"] || 0) > 0);
  if (!batches.length) return null;

  const start = async (b, takeover = false) => {
    setBusy(b.id + "start");
    try { const { data } = await api.post(`/reports/batch/${b.id}/start${takeover ? "?takeover=true" : ""}`); bumpUnread(); toast.success(`You're on it — ${data.updated} request(s) marked in-progress`); onChanged(); }
    catch (err) {
      if (err?.response?.status === 409 && window.confirm(`${err.response.data.detail}. Take it over?`)) return start(b, true);
      toast.error(formatApiError(err));
    } finally { setBusy(null); }
  };

  const setStatus = async (b, status) => {
    if (!window.confirm(`Mark all ${b.count} requests in "${b.label}" as ${status}?`)) return;
    setBusy(b.id + status);
    try { const { data } = await api.patch(`/reports/batch/${b.id}`, { status }); toast.success(`${data.updated} request(s) marked ${status}`); onChanged(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  const uploadZip = async (b, f) => {
    if (!f) return;
    const complete = window.confirm(`Attach results from "${f.name}" to "${b.label}".\n\nFiles are matched to drivers by name or license number in the filename.\n\nOK = also mark matched requests Completed · Cancel = attach only`);
    setBusy(b.id + "zip");
    try {
      const fd = new FormData(); fd.append("file", f); fd.append("mark_completed", complete ? "true" : "false");
      const { data } = await api.post(`/reports/batch/${b.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extra = [...data.unmatched.map((u) => `${u} (no match)`), ...data.ambiguous.map((a) => `${a.file} (matches ${a.candidates.join(", ")})`)];
      toast[extra.length ? "warning" : "success"](`${data.matched.length} of ${data.matched.length + extra.length} files attached${extra.length ? ` · not attached: ${extra.join("; ")}` : ""}`, { duration: extra.length ? 15000 : 5000 });
      onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <Card className="mb-4 overflow-hidden" data-testid="batch-bar">
      <div className="px-5 py-2.5 border-b border-slate-200 bg-slate-50 tag-mono text-slate-500 flex items-center gap-2"><Layers className="h-3.5 w-3.5" />OPEN BULK BATCHES</div>
      <div className="divide-y divide-slate-100">
        {batches.map((b) => (
          <div key={b.id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid={`batch-row-${b.id}`}>
            <div className="min-w-0">
              <div className="font-medium text-slate-900 truncate">{b.label || "Bulk request"} <span className="text-slate-400 font-normal">· {b.company}</span></div>
              <div className="text-xs text-slate-500 font-mono">{new Date(b.created_at).toLocaleDateString()} · {b.count} driver(s) · {b.types.join(", ")} · {Object.entries(b.statuses).map(([s, n]) => `${n} ${s}`).join(", ")}</div>
              <div className="text-xs mt-1" data-testid={`batch-owners-${b.id}`}>{Object.values(b.owners).length === 0 ? <span className="text-slate-400 italic">Nobody has started this yet</span> : Object.values(b.owners).map((o) => <span key={o.id} className="inline-flex items-center gap-1 mr-3 text-slate-700"><span className="h-4 w-4 rounded-full bg-blue-700 text-white text-[9px] font-bold inline-flex items-center justify-center">{o.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</span>{o.id === me?.id ? "You" : o.name.split(" ")[0]} started · {new Date(o.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>)}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(b.statuses.pending || 0) > 0 && !b.owners[me?.id] && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={!!busy} onClick={() => start(b)} data-testid={`batch-start-${b.id}`}>{busy === b.id + "start" ? "Starting…" : (Object.keys(b.owners).length ? "Take over" : "Start working")}</Button>}
              <label className="inline-flex" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && f.name.toLowerCase().endsWith(".zip")) uploadZip(b, f); }}><Button size="sm" variant="outline" disabled={!!busy} asChild><span className="cursor-pointer" data-testid={`batch-zip-${b.id}`}><Upload className="h-3.5 w-3.5 mr-1" />{busy === b.id + "zip" ? "Attaching…" : "Upload results ZIP (or drop it here)"}</span></Button>
                <input type="file" accept=".zip,application/zip" className="hidden" onChange={(e) => { uploadZip(b, e.target.files?.[0]); e.target.value = ""; }} data-testid={`batch-zip-input-${b.id}`} /></label>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!!busy} onClick={() => setStatus(b, "completed")} data-testid={`batch-complete-${b.id}`}>{busy === b.id + "completed" ? "Updating…" : "Mark all completed"}</Button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function ResultFiles({ report, isAdmin, onChange }) {
  const [uploading, setUploading] = useState(false);
  const files = report.files || [];

  const upload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/reports/${report.id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${f.name} attached · customer notified`); onChange(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };
  const open = async (f) => {
    try {
      const res = await api.get(`/reports/${report.id}/files/${f.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.name}?`)) return;
    try { const { data } = await api.delete(`/reports/${report.id}/files/${f.id}`); onChange(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="rounded-md border border-slate-200 p-3 space-y-2" data-testid="report-results-section">
      <div className="tag-mono text-slate-500">FINISHED REPORTS</div>
      {files.length === 0 && <div className="text-xs text-slate-500">{isAdmin ? "No files attached yet." : "Results will appear here once the report is finished."}</div>}
      {files.map((f) => (
        <div key={f.id} data-testid={`report-file-${f.id}`}>
        <div className="flex items-center justify-between gap-2 text-sm">
          <button type="button" className="inline-flex items-center gap-1 text-blue-700 hover:underline truncate" onClick={() => open(f)} data-testid={`report-file-download-${f.id}`}><FileText className="h-4 w-4 shrink-0" />{f.name}</button>
          <span className="text-xs text-slate-400 font-mono shrink-0">{(f.size / 1024).toFixed(0)} KB · {new Date(f.uploaded_at).toLocaleDateString()}</span>
          {isAdmin && <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => remove(f)} data-testid={`report-file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
        {isAdmin && <LicenseSuggestion report={report} file={f} onReportChange={onChange} />}
        {isAdmin && <ExtractViolationsButton report={report} file={f} onReportChange={onChange} />}
        </div>
      ))}
      {isAdmin && (
        <label className="inline-flex items-center gap-2 text-sm text-blue-700 cursor-pointer hover:underline" data-testid="report-file-upload-label" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f && !uploading) upload(f); }}>
          <Upload className="h-4 w-4" />{uploading ? "Uploading…" : "Attach finished PDF (or drop it here)"}
          <input type="file" className="hidden" accept="application/pdf,image/png,image/jpeg,image/webp" disabled={uploading} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="report-file-input" />
        </label>
      )}
      {isAdmin && <ScanButton onFile={upload} disabled={uploading} testid="report-file-scan" className="ml-3 text-sm" />}
    </div>
  );
}

function ReportDetailDialog({ report: initial, onClose, isAdmin, customers = [] }) {
  const [report, setReport] = useState(initial);
  const [rebilling, setRebilling] = useState(false);
  const rebill = async (customerId) => {
    setRebilling(true);
    try { const { data } = await api.patch(`/reports/${report.id}/bill-to`, { customer_id: customerId }); setReport(data); toast.success(`Now billed to ${data.requester_company || data.requester_name}`); }
    catch (err) { toast.error(formatApiError(err)); } finally { setRebilling(false); }
  };
  const [status, setStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [resultUrl, setResultUrl] = useState("");
  const [cost, setCost] = useState("");
  const [billed, setBilled] = useState("");
  const [costNotes, setCostNotes] = useState("");
  const [lines, setLines] = useState([]);
  const [linesDirty, setLinesDirty] = useState(false);
  const { catalog, statePrices } = useCatalog();
  const [driverState, setDriverState] = useState(null);

  useEffect(() => {
    setReport(initial);
    if (initial) {
      setStatus(initial.status);
      setNotes(initial.admin_notes || "");
      setResultUrl(initial.result_url || "");
      setCost(initial.cost ?? "");
      setBilled(initial.total ?? "");
      setCostNotes(initial.cost_notes || "");
      setLines((initial.line_items || initial.report_types.map((c) => ({ code: c, label: c, qty: 1, unit_price: initial.unit_prices?.[c] ?? 0 }))).map((l) => ({ ...l })));
      setLinesDirty(false);
      setDriverState(null);
      if (initial.driver_id) api.get(`/drivers/${initial.driver_id}`).then(({ data }) => setDriverState(data.license_state)).catch(() => {});
    }
  }, [initial]);

  const { user: me } = useAuth();
  const [starting, setStarting] = useState(false);

  if (!report) return null;

  const save = async () => {
    try {
      if (isAdmin && linesDirty) {
        const { data } = await api.patch(`/reports/${report.id}/lines`, { lines: toLinePayload(lines) });
        setBilled(data.total);
      }
      await api.patch(`/reports/${report.id}`, {
        status, admin_notes: notes, result_url: resultUrl,
        cost: cost === "" ? null : Number(cost),
        total: linesDirty ? null : (billed === "" ? null : Number(billed)),
        cost_notes: costNotes,
      });
      toast.success("Updated");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    if (!window.confirm("Permanently delete this report request? It will also disappear from billing.")) return;
    try {
      await api.delete(`/reports/${report.id}`);
      toast.success("Request deleted");
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const canDelete = isAdmin || report.status === "pending";
  const openReq = report.status === "pending" || report.status === "in-progress";
  const mine = report.started_by?.id === me?.id;
  const start = async (takeover = false) => {
    setStarting(true);
    try { const { data } = await api.post(`/reports/${report.id}/start${takeover ? "?takeover=true" : ""}`); setReport(data); setStatus(data.status); bumpUnread(); toast.success(takeover ? "You took over this request" : "You're on it — marked in-progress"); }
    catch (err) {
      if (err?.response?.status === 409 && window.confirm(`${err.response.data.detail}. Take it over?`)) return start(true);
      toast.error(formatApiError(err));
    } finally { setStarting(false); }
  };

  return (
    <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5" />Request Detail</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {report.driver_id && <DriverInfoCard driverId={report.driver_id} isAdmin={isAdmin} />}
          {isAdmin && (
            <div className="rounded-md border border-slate-200 p-3 flex items-center gap-3 flex-wrap" data-testid="bill-to-panel">
              <div className="min-w-0"><div className="tag-mono text-slate-500">BILL TO</div><div className="text-sm font-medium truncate">{report.requester_company || report.requester_name}</div>{report.bill_to_changed && <div className="text-[11px] text-slate-500">moved from {report.bill_to_changed.from} by {report.bill_to_changed.by}</div>}</div>
              <div className="ml-auto w-56">
                <Select value={customers.some((c) => c.id === report.requester_id) ? report.requester_id : ""} onValueChange={rebill} disabled={rebilling}>
                  <SelectTrigger data-testid="bill-to-select"><SelectValue placeholder="Change company…" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="report-progress-panel">
            <ProgressBar r={report} />
            {isAdmin && openReq && (
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                {report.started_by ? (
                  <span className="text-xs text-slate-700" data-testid="report-started-by"><b>{mine ? "You" : report.started_by.name}</b> started · {new Date(report.started_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{report.taken_over_from && <span className="text-slate-400"> (took over from {report.taken_over_from.name})</span>}</span>
                ) : <span className="text-xs text-slate-500 italic">Nobody has started this yet</span>}
                {!report.started_by && <Button size="sm" className="bg-blue-700 hover:bg-blue-800" disabled={starting} onClick={() => start(false)} data-testid="report-start-button"><Play className="h-3.5 w-3.5 mr-1" />{starting ? "Starting…" : "Start working"}</Button>}
                {report.started_by && !mine && <Button size="sm" variant="outline" disabled={starting} onClick={() => start(true)} data-testid="report-takeover-button">Take over</Button>}
              </div>
            )}
            {!isAdmin && report.started_by && <div className="mt-2 text-xs text-slate-700" data-testid="report-customer-started-by">Started by <b>{report.started_by.name.split(" ")[0]}</b> · {new Date(report.started_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>}
            {!isAdmin && !report.started_by && openReq && <div className="mt-2 text-xs text-slate-500 italic">Received — Safer Highways will start on this shortly.</div>}
          </div>
          <div><span className="tag-mono text-slate-500 block">Driver</span>{report.driver_name || "—"}</div>
          <div><span className="tag-mono text-slate-500 block">Reports</span>{displayTypes(report.report_types).join(", ")}{report.line_items?.some((l) => l.code.startsWith("BGC")) && <span className="text-xs text-slate-500"> · bundle: {report.line_items.filter((l) => l.code.startsWith("BGC")).map((l) => `${l.label.replace(/\s*\(per .*\)/, "")}${l.qty > 1 ? ` ×${l.qty}` : ""}`).join(", ")}</span>}{report.rush && " (RUSH)"}{report.source === "off-portal" && <Badge className="ml-2 bg-violet-50 text-violet-700 border border-violet-200" data-testid="report-offportal-badge">Off-portal · {report.channel}</Badge>}</div>
          {report.notes && <div><span className="tag-mono text-slate-500 block">Requester Notes</span>{report.notes}</div>}
          {report.bgc_scope && (
            <div className="rounded-md border border-sky-200 bg-sky-50 p-3" data-testid="report-bgc-scope">
              <span className="tag-mono text-sky-800 block">Background Check Scope</span>
              <div className="text-sm text-slate-900">{report.bgc_scope.street}, {report.bgc_scope.city}, {report.bgc_scope.county} County, {report.bgc_scope.state} {report.bgc_scope.zip}</div>
              <div className="text-xs text-slate-600 mt-0.5">SSN ending ···{report.bgc_scope.ssn_last4}{isAdmin && " · full SSN on the driver record"}</div>
            </div>
          )}
          <ResultFiles report={report} isAdmin={isAdmin} onChange={setReport} />
          {isAdmin ? (
            <>
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="report-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Result URL (optional external link)</Label><Input value={resultUrl} onChange={(e) => setResultUrl(e.target.value)} placeholder="https://..." /></div>
              <div className="rounded-md border border-slate-200 p-3 space-y-2 bg-slate-50" data-testid="report-lines-section">
                <div className="flex items-center justify-between"><div className="tag-mono text-slate-500">BILLABLE LINE ITEMS</div>{driverState && <span className="text-xs text-blue-700">MVR uses {driverState} state fee</span>}</div>
                <LineItemsEditor lines={lines} onChange={(l) => { setLines(l); setLinesDirty(true); setBilled(linesTotal(l, report.rush ? report.rush_fee : 0).toFixed(2)); }} catalog={catalog} statePrices={statePrices} driverState={driverState} testid="report-lines" />
                <div className="text-xs text-slate-500">Set county / state quantities here when you fulfill a background check. {report.rush ? `Rush fee $${Number(report.rush_fee || 0).toFixed(2)} is added on top.` : ""}</div>
              </div>
              <div className="rounded-md border border-slate-200 p-3 space-y-2 bg-slate-50" data-testid="report-cost-section">
                <div className="tag-mono text-slate-500">COST TRACKING (admin only)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Your cost ($)</Label><Input type="number" step="0.01" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 9.50" data-testid="report-cost-input" /></div>
                  <div><Label>Amount billed ($)</Label><Input type="number" step="0.01" min="0" value={billed} disabled={linesDirty} title={linesDirty ? "Computed from line items" : "Override the billed total"} onChange={(e) => setBilled(e.target.value)} data-testid="report-billed-input" /></div>
                </div>
                {cost !== "" && billed !== "" && (
                  <div className="text-xs font-mono text-slate-600" data-testid="report-margin">Margin: ${(Number(billed) - Number(cost)).toFixed(2)}</div>
                )}
                <Input value={costNotes} onChange={(e) => setCostNotes(e.target.value)} placeholder="Vendor / invoice ref (optional)" data-testid="report-cost-notes-input" />
              </div>
              <div><Label>Admin Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              <DialogFooter className="flex justify-between sm:justify-between">
                <Button variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={remove} data-testid="report-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
                <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} data-testid="report-save-status-button">Save</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div><span className="tag-mono text-slate-500 block">Status</span>{statusBadge(report.status)}</div>
              {report.total != null && <div><span className="tag-mono text-slate-500 block">Estimated charge</span><span className="font-mono" data-testid="report-detail-estimate">${Number(report.total).toFixed(2)}</span> <span className="text-xs text-slate-500">· reference only, invoiced via QuickBooks</span></div>}
              {report.admin_notes && <div><span className="tag-mono text-slate-500 block">Admin Notes</span>{report.admin_notes}</div>}
              {report.result_url && <a className="text-blue-700 underline text-sm" href={report.result_url} target="_blank" rel="noreferrer">Download result</a>}
              {canDelete && <DialogFooter><Button variant="ghost" className="text-rose-600 hover:text-rose-700" onClick={remove} data-testid="report-delete-button"><Trash2 className="h-4 w-4 mr-1" />Delete request</Button></DialogFooter>}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
